import { useCallback, useState } from "react";

/** Where the prototype keeps the same state (design/account/account.js). */
const STORAGE_KEY = "pc.nav.folded";

type FoldedGroups = Record<string, boolean>;

/**
 * Reading and writing are both guarded: a browser with storage blocked still
 * gets a working menu, it just forgets which groups were folded.
 */
const readFolded = (): FoldedGroups => {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}");

    return typeof parsed === "object" && parsed !== null ? (parsed as FoldedGroups) : {};
  } catch {
    return {};
  }
};

const writeFolded = (folded: FoldedGroups): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(folded));
  } catch {
    // Storage is a convenience here, never a requirement.
  }
};

/** Which sidebar groups are collapsed, remembered between visits. */
export const useFoldedGroups = () => {
  const [folded, setFolded] = useState<FoldedGroups>(readFolded);

  const toggleFold = useCallback((key: string) => {
    setFolded((current) => {
      const next = { ...current, [key]: current[key] !== true };

      writeFolded(next);

      return next;
    });
  }, []);

  return { folded, toggleFold };
};
